import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { LogOut, Menu, X } from "lucide-react";
import NotificationBell from "./NotificationBell";

const initials = (name) =>
  name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const navLinks = [
    { label: "Opportunities", to: "/opportunities" },
    { label: "Professionals", to: "/professionals" },
  ];
  if (user?.role === "admin") {
    navLinks.length = 0;
    navLinks.push({ label: "Opportunities", to: "/opportunities" });
    navLinks.push({ label: "Post Community", to: "/admin" });
    navLinks.push({ label: "Community Inbox", to: "/admin/inbox" });
  } else {
    if (user) navLinks.push({ label: "Dashboard", to: "/dashboard" });
    if (user?.role === "organisation") navLinks.push({ label: "Post a Requirement", to: "/post-requirement" });
    if (user?.role === "professional") navLinks.push({ label: "Saved", to: "/saved" });
    if (user) navLinks.push({ label: "Messages", to: "/messages" });
  }

  const isActive = (to) => location.pathname === to;

  const handleLogout = () => {
    logout();
    setOpen(false);
    navigate("/");
  };

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-100 bg-white/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1200px] items-center justify-between px-4 py-3 sm:px-6 sm:py-4 lg:px-8">
        <Link to="/">
          <Logo size={44} />
        </Link>

        <nav className="flex items-center gap-3 sm:gap-5">
          {/* Desktop nav links */}
          <div className="hidden items-center gap-5 lg:flex">
            {navLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`text-[15px] font-medium transition-colors hover:text-[#1E4FA3] ${
                  isActive(item.to) ? "text-[#1E4FA3]" : "text-slate-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Desktop auth group */}
          <div className="hidden items-center gap-3 lg:flex">
            {user ? (
              <>
                <NotificationBell />
                <Link to={user?.role === "admin" ? "/admin" : "/dashboard"} className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E4FA3]/10 font-heading text-[13px] font-bold text-[#1E4FA3]">
                    {initials(user.name)}
                  </div>
                  <span className="text-sm font-semibold text-[#0B2545]">{user.name.split(" ")[0]}</span>
                </Link>
                <Button variant="outline" onClick={handleLogout} className="h-10 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-800">
                  <LogOut className="mr-1.5 h-4 w-4" /> Logout
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => navigate("/login")} className="h-11 rounded-xl border-2 border-[#1E4FA3]/25 bg-white px-6 text-[15px] font-semibold text-[#1E4FA3] hover:bg-[#1E4FA3]/5 hover:text-[#1E4FA3]">Login</Button>
                <Button onClick={() => navigate("/register")} className="h-11 rounded-xl bg-[#16A34A] px-7 text-[15px] font-semibold text-white shadow-sm hover:bg-[#138a3f]">Register</Button>
              </>
            )}
          </div>

          {/* Mobile: avatar + hamburger */}
          <div className="flex items-center gap-2 lg:hidden">
            {user && <NotificationBell />}
            {user && (
              <Link to={user?.role === "admin" ? "/admin" : "/dashboard"} className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E4FA3]/10 font-heading text-[13px] font-bold text-[#1E4FA3]">
                {initials(user.name)}
              </Link>
            )}
            <button
              onClick={() => setOpen((o) => !o)}
              aria-label="Menu"
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile menu panel */}
      {open && (
        <div className="border-t border-slate-100 bg-white px-4 pb-4 pt-2 lg:hidden">
          <div className="flex flex-col">
            {navLinks.map((item) => (
              <button
                key={item.to}
                onClick={() => go(item.to)}
                className={`rounded-lg px-3 py-3 text-left text-[15px] font-medium transition-colors hover:bg-slate-50 ${
                  isActive(item.to) ? "text-[#1E4FA3]" : "text-slate-700"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="mt-3 border-t border-slate-100 pt-3">
            {user ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#1E4FA3]/10 font-heading text-[13px] font-bold text-[#1E4FA3]">{initials(user.name)}</div>
                  <span className="text-sm font-semibold text-[#0B2545]">{user.name}</span>
                </div>
                <Button variant="outline" onClick={handleLogout} className="h-10 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  <LogOut className="mr-1.5 h-4 w-4" /> Logout
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => go("/login")} className="h-11 flex-1 rounded-xl border-2 border-[#1E4FA3]/25 bg-white text-[15px] font-semibold text-[#1E4FA3] hover:bg-[#1E4FA3]/5">Login</Button>
                <Button onClick={() => go("/register")} className="h-11 flex-1 rounded-xl bg-[#16A34A] text-[15px] font-semibold text-white hover:bg-[#138a3f]">Register</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
